import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaUserCircle, FaSignOutAlt, FaBars, FaSearch, FaQuestionCircle, FaMoon, FaSun, FaLanguage, FaHome, FaBell, FaCalendarCheck, FaEnvelope } from 'react-icons/fa';
import { signOut } from 'firebase/auth';
import { collection, getDocs, doc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { auth, db } from '../config/firebaseConfig';
import { HEARTBEAT_INTERVAL_MS } from '../utils/presence';
import { accumulateDailyActivity, markDailySession, MAX_FLUSH_MS } from '../utils/onlineActivity';
import { useUserProfile } from '../contexts/UserProfileContext';
import { usePendingLeaves } from '../contexts/PendingLeavesContext';
import { useMessageNotifications } from './MessageNotifications';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { menuItems } from './SideBar';
import MyTasksModal from '../pages/Dashboard/MyTasksModal';
import '../style/TopBar.css';

// Profile image URLs that already failed to preload. Kept outside the component
// so the guard survives TopBar remounts (see the preload effect below).
const failedProfileImageUrls = new Set();

// Last presence heartbeat, at module scope for the same reason: TopBar remounts
// on every route change and the throttle must survive that.
let lastHeartbeatAt = 0;

// `pagehide` yields a logoutTime stamp on tab close. If the page comes back
// (reload, or bfcache restore), the first heartbeat has to clear that stamp or
// the user would look offline until the next tick. Module scope resets on a
// real page load, which is exactly the lifetime we want.
let hasClearedLogoutThisPageLoad = false;

// Nabızla birlikte yazılan son rota. Modül kapsamında tutulur çünkü TopBar her
// rota değişiminde yeniden mount oluyor; aynı yolu tekrar tekrar yazmamak için
// bu değerin remount'lara dayanması gerekiyor.
let lastWrittenPath = null;

const TopBar = ({ pageTitle, onToggleSidebar, isMobile }) => {
  const { profileImageUrl, employeeData, employeeId, refetchProfileData, isLoading } = useUserProfile();
  const { isDarkMode, toggleTheme } = useTheme();
  const { pendingLeaveCount } = usePendingLeaves();
  const { unreadCount: unreadMessageCount } = useMessageNotifications();
  const location = useLocation();
  // Nabız fonksiyonu efekt içinde kapanış (closure) olarak yaşıyor; güncel
  // rotayı ref üzerinden okur ki efektin yeniden kurulması gerekmesin.
  const currentPathRef = useRef(location.pathname);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  const [dropdownImageLoaded, setDropdownImageLoaded] = useState(false);
  const [cachedProfileUrl, setCachedProfileUrl] = useState('');
  const [unseenCount, setUnseenCount] = useState(0);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnseenCount = useCallback(async () => {
    if (!employeeId) return;
    try {
      const snapshot = await getDocs(collection(db, 'Tasks'));
      const count = snapshot.docs.reduce((acc, d) => {
        const task = d.data();
        const isMine =
          task.assigneeId === employeeId ||
          (task.assignees && task.assignees.some(a => a.id === employeeId));
        if (!isMine) return acc;
        if (task.status === 'completed' || task.status === 'cancelled') return acc;
        const seen = Array.isArray(task.viewedBy) && task.viewedBy.includes(employeeId);
        return seen ? acc : acc + 1;
      }, 0);
      setUnseenCount(count);
    } catch (err) {
      console.error('Error fetching unseen tasks:', err);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchUnseenCount();
    const interval = setInterval(fetchUnseenCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnseenCount]);

  // Presence heartbeat. TopBar remounts on every route change (Layout wraps each
  // route), so this deliberately does NOT write logoutTime on unmount: doing so
  // raced the next mount's login write and could flip an actively-navigating
  // user offline. Instead the client refreshes `lastSeen` on a timer and the
  // reader treats a stale stamp as offline. See utils/presence.js.
  //
  // Two layers mark a user offline, in order of speed:
  //   1. `pagehide` writes logoutTime on tab/browser close — instant, but the
  //      browser may kill the request before it lands, so it is best-effort.
  //   2. The heartbeat going stale past ONLINE_TTL_MS — always works, including
  //      after a crash, a force-quit or a dropped connection. This is the
  //      guarantee; layer 1 is only an optimisation on top of it.
  //
  // sessionStartTime/sessionCount/loginTime are written once per browser-tab
  // session via a sessionStorage flag. totalOnlineMs accumulates elapsed time
  // for usage statistics.
  useEffect(() => {
    if (!employeeId) return;
    const employeeRef = doc(db, 'Employees', employeeId);
    const sessionKey = `hb_session_started_${employeeId}`;
    let lastFlush = Date.now();

    const sendHeartbeat = async ({ force = false } = {}) => {
      const now = Date.now();
      // Rota değiştiğinde nabız kısıtı atlanır: "şu an hangi sayfada" bilgisi
      // ancak bu şekilde anlık kalır. Rota değişimi kullanıcı hızında olduğu
      // için bu ekstra yazma sayısını anlamlı biçimde artırmaz.
      const pathChanged = currentPathRef.current !== lastWrittenPath;
      // Throttled at module scope so a burst of route changes does not turn
      // into a burst of writes.
      if (!force && !pathChanged && now - lastHeartbeatAt < HEARTBEAT_INTERVAL_MS) return;
      lastHeartbeatAt = now;

      try {
        const updates = { lastSeen: serverTimestamp() };
        // Sayfa Yetkileri ekranındaki "Çevrimiçi Kullanıcılar" listesi bu iki
        // alanı okuyup kullanıcının o an hangi menüde olduğunu gösterir.
        if (pathChanged) {
          lastWrittenPath = currentPathRef.current;
          updates.currentPath = currentPathRef.current;
          updates.currentPathAt = serverTimestamp();
        }
        if (!hasClearedLogoutThisPageLoad) {
          hasClearedLogoutThisPageLoad = true;
          // Sekme kapanışında yazılmış olabilecek çıkış damgasını sil. Sayfa
          // yenilendiğinde sessionStorage bayrağı durduğu için aşağıdaki
          // "yeni oturum" bloğu çalışmaz; temizlik bu yüzden ayrı.
          updates.logoutTime = null;
        }
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, String(now));
          updates.loginTime = serverTimestamp();
          updates.sessionStartTime = serverTimestamp();
          updates.sessionCount = increment(1);
          // Ömür boyu sayacın gün bazlı karşılığı; ekip raporları bunu okur.
          markDailySession(employeeId, now);
        }
        await updateDoc(employeeRef, updates);
      } catch (error) {
        console.debug('Failed to send presence heartbeat:', error);
      }
    };

    const flushOnlineTime = () => {
      const now = Date.now();
      const rawElapsed = now - lastFlush;
      lastFlush = now;
      if (rawElapsed < 1000) return;
      // Uyku modunda zamanlayıcı durur; uyanışta arada saatler görünebilir.
      // Gerçekte sistemde geçirilmemiş bu süre toplamları şişirmesin diye
      // tek seferde eklenebilecek süre kırpılır.
      const elapsed = Math.min(rawElapsed, MAX_FLUSH_MS);
      updateDoc(employeeRef, { totalOnlineMs: increment(elapsed) }).catch((error) => {
        console.debug('Failed to flush online time:', error);
      });
      // Aynı süre bir de güne bölünmüş olarak yazılır; ömür boyu sayaç tarih
      // aralığına göre raporlanamadığı için (bkz. utils/onlineActivity.js).
      accumulateDailyActivity(employeeId, now - elapsed, now);
    };

    sendHeartbeat();
    const heartbeatInterval = setInterval(() => sendHeartbeat({ force: true }), HEARTBEAT_INTERVAL_MS);
    const flushInterval = setInterval(flushOnlineTime, 5 * 60 * 1000);

    // Coming back to the tab should refresh presence immediately rather than
    // waiting out the remainder of the interval.
    const handleVisibilityChange = () => {
      if (!document.hidden) sendHeartbeat();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Sekme/tarayıcı kapanışında anında çevrimdışı görünmek için son bir deneme.
    // `pagehide`, `beforeunload`'ın aksine mobilde de tetiklenir ve YALNIZCA
    // gerçek sayfa yıkımında çalışır — React rota değişimlerinde çalışmadığı
    // için eskiden yaşanan "gezinirken çevrimdışı düşme" yarışı geri gelmez.
    // Yazının tamamlanacağı garanti değildir; garanti eden şey nabız TTL'idir.
    const handlePageHide = () => {
      lastHeartbeatAt = 0;
      hasClearedLogoutThisPageLoad = false;
      updateDoc(employeeRef, { logoutTime: serverTimestamp() }).catch(() => {});
    };
    window.addEventListener('pagehide', handlePageHide);

    // Geri/ileri ile bfcache'ten dönen sayfa yeni bir yükleme sayılmaz; nabzı
    // elle tazelemezsek kullanıcı çevrimdışı görünmeye devam eder.
    const handlePageShow = (event) => {
      if (event.persisted) sendHeartbeat({ force: true });
    };
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(flushInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      flushOnlineTime();
    };
  }, [employeeId]);

  // Rota her değiştiğinde nabzı tazele; böylece `currentPath` anlık kalır.
  useEffect(() => {
    currentPathRef.current = location.pathname;
    if (!employeeId) return;
    if (location.pathname === lastWrittenPath) return;
    lastWrittenPath = location.pathname;
    updateDoc(doc(db, 'Employees', employeeId), {
      lastSeen: serverTimestamp(),
      currentPath: location.pathname,
      currentPathAt: serverTimestamp(),
    }).catch((error) => {
      console.debug('Failed to write current path:', error);
    });
  }, [employeeId, location.pathname]);

  const handleOpenMyTasks = () => setShowMyTasks(true);
  const handleCloseMyTasks = () => {
    setShowMyTasks(false);
    fetchUnseenCount();
  };

  // Cache the profile image URL when it's valid
  useEffect(() => {
    if (profileImageUrl && profileImageUrl !== cachedProfileUrl) {
      // Pre-load the image to check if it's valid
      const img = new Image();
      img.onload = () => {
        setCachedProfileUrl(profileImageUrl);
        setMainImageLoaded(true);
      };
      img.onerror = () => {
        console.log("Failed to pre-load profile image");
        // Retry the fetch at most once per URL. The set lives at module scope
        // because TopBar remounts whenever the profile refetch re-renders the
        // tree — a component ref would be wiped and the retry would loop.
        if (!cachedProfileUrl && !failedProfileImageUrls.has(profileImageUrl)) {
          failedProfileImageUrls.add(profileImageUrl);
          refetchProfileData();
        }
      };
      img.src = profileImageUrl;
    }
  }, [profileImageUrl, cachedProfileUrl, refetchProfileData]);

  // Note: Profile data is already fetched by UserProfileContext on auth state change.
  // Removed redundant refetchProfileData() call that fired on every mount.

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Use cached profile URL if available, otherwise use the current one
  const displayProfileUrl = cachedProfileUrl || profileImageUrl;

  // Search functionality
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (term.trim() === '') {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    const userHasAuth = (requiredAuth, path) => {
      if (employeeData?.authorization?.includes('Kısıtlı Admin')) {
        // For Kısıtlı Admin, skip search results filtering here (optional: fetch Firestore map if needed)
        return true;
      }
      if (!requiredAuth || requiredAuth.length === 0) return true;
      const userAuth = employeeData?.authorization || [];
      return requiredAuth.some(r => userAuth.includes(r));
    };

    const results = [];
    menuItems.forEach(item => {
      if (!userHasAuth(item.requiredAuth, item.path)) return;
      // Search in main menu items
      if (item.title.toLowerCase().includes(term.toLowerCase())) {
        results.push({
          title: item.title,
          path: item.path,
          icon: item.icon,
          isMainItem: true
        });
      }
      
      // Search in submenu items
      if (item.submenu) {
        item.submenu.forEach(subItem => {
          if (!userHasAuth(subItem.requiredAuth || item.requiredAuth, subItem.path)) return;
          if (subItem.title.toLowerCase().includes(term.toLowerCase())) {
            results.push({ 
              ...subItem, 
              parentTitle: item.title,
              isMainItem: false
            });
          }
        });
      }
    });

    setSearchResults(results);
    setShowResults(results.length > 0);
  };

  const handleResultClick = (path) => {
    navigate(path);
    setSearchTerm('');
    setShowResults(false);
  };

  const handleProfileClick = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleLogout = async () => {
    try {
      // Yeni girişin yeni oturum sayılması için oturum bayrağını temizle
      if (employeeId) sessionStorage.removeItem(`hb_session_started_${employeeId}`);
      // Açık çıkışta nabzın eskimesini beklemeden çevrimdışı görünsün.
      // Nabız atmayı da durdur ki sonraki mount hemen yeniden yazmasın.
      if (employeeId) {
        lastHeartbeatAt = Date.now();
        try {
          await updateDoc(doc(db, 'Employees', employeeId), {
            logoutTime: serverTimestamp(),
            lastSeen: null
          });
        } catch (error) {
          console.debug('Failed to clear presence on logout:', error);
        }
      }
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  const handleProfileNavigation = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  return (
    <header className={`topbar ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="topbar-left">
        {isMobile && (
          <button className="menu-toggle" onClick={onToggleSidebar}>
            <FaBars />
          </button>
        )}
        <h1 className="page-title">{pageTitle}</h1>
      </div>

      <div className="topbar-search" ref={searchRef}>
        <div className="search-wrapper">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Menülerde ara..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {showResults && (
          <div className="search-results">
            {searchResults.length === 0 ? (
              <div className="no-results">Sonuç bulunamadı</div>
            ) : (
              searchResults.map((result, index) => (
                <div 
                  key={index} 
                  className="search-result-item"
                  onClick={() => handleResultClick(result.path)}
                >
                  <span className="result-icon">{result.icon}</span>
                  <div className="result-text">
                    <span className="result-title">{result.title}</span>
                    {!result.isMainItem && (
                      <span className="result-parent">{result.parentTitle}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="topbar-right">
        {/* Home button */}
        <button 
          className="icon-button" 
          onClick={() => navigate('/dashboard')}
          title="Ana Sayfa"
        >
          <FaHome />
        </button>

        {/* Notifications (Tasks) */}
        <button
          className={`icon-button notification-button ${unseenCount > 0 ? 'has-unseen' : ''}`}
          onClick={handleOpenMyTasks}
          title={unseenCount > 0 ? `${unseenCount} yeni görev` : 'Görevlerim'}
          aria-label="Görevlerim"
        >
          <FaBell />
          {unseenCount > 0 && (
            <span className="notification-badge">{unseenCount > 99 ? '99+' : unseenCount}</span>
          )}
        </button>

        {/* Okunmamış Mesajlar */}
        {unreadMessageCount > 0 && (
          <button
            className="icon-button notification-button has-unseen"
            onClick={() => navigate('/profile', {
              state: { activeTab: 'mesajlar', requestedAt: Date.now() }
            })}
            title={`${unreadMessageCount} okunmamış mesaj`}
            aria-label="Okunmamış mesajlar"
          >
            <FaEnvelope />
            <span className="notification-badge">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
          </button>
        )}

        {/* Bekleyen İzin Talepleri Bildirimi */}
        {pendingLeaveCount > 0 && (
          <button
            className="icon-button notification-button has-unseen"
            onClick={() => navigate('/izin-yonetimi')}
            title={`${pendingLeaveCount} bekleyen izin talebi`}
            aria-label="Bekleyen izin talepleri"
          >
            <FaCalendarCheck />
            <span className="notification-badge">{pendingLeaveCount > 99 ? '99+' : pendingLeaveCount}</span>
          </button>
        )}

        {/* Theme Toggle */}
        <button
          className="icon-button theme-toggle"
          onClick={toggleTheme}
          title={isDarkMode ? "Light Mode" : "Dark Mode"}
        >
          {isDarkMode ? <FaSun /> : <FaMoon />}
        </button>

        {/* Help button */}
        <button className="icon-button">
          <FaQuestionCircle />
        </button>

        {/* Translate button */}
        <button className="icon-button">
          <FaLanguage />
        </button>

        {/* User profile */}
        <div className="user-profile" ref={userMenuRef}>
          <div className="user-button-container">
            <button className="user-avatar-button" onClick={handleProfileClick}>
              {displayProfileUrl ? (
                <img 
                  src={displayProfileUrl} 
                  alt="Profile" 
                  className="profile-image"
                  onLoad={() => setMainImageLoaded(true)}
                  onError={(e) => {
                    console.log("Error loading profile image, using fallback");
                    e.target.onerror = null; // Prevent infinite callback loop
                    e.target.style.display = 'none'; // Hide the broken image
                    if (cachedProfileUrl === displayProfileUrl) {
                      setCachedProfileUrl(''); // Clear cached URL if it's invalid
                      refetchProfileData();
                    }
                  }}
                />
              ) : (
                <FaUserCircle className="profile-icon" />
              )}
            </button>
            <button className="user-name-button" onClick={handleProfileClick}>
              <span className="user-name">
                {employeeData ? `${employeeData.eName} ${employeeData.eSurname}` : 'Kullanıcı'}
              </span>
            </button>
          </div>

          {showUserMenu && (
            <div className="user-dropdown" style={{ overflow: 'visible' }}>
              <div className="user-info">
                <div className="user-avatar">
                  {displayProfileUrl ? (
                    <img 
                      src={displayProfileUrl} 
                      alt="Profile" 
                      onLoad={() => setDropdownImageLoaded(true)}
                      onError={(e) => {
                        console.log("Error loading profile image in dropdown, using fallback");
                        e.target.onerror = null; // Prevent infinite callback loop
                        e.target.style.display = 'none'; // Hide the broken image
                        if (cachedProfileUrl === displayProfileUrl) {
                          setCachedProfileUrl(''); // Clear cached URL if it's invalid
                          refetchProfileData();
                        }
                      }}
                    />
                  ) : (
                    <FaUserCircle />
                  )}
                </div>
                <div className="user-details">
                  <h3>
                    {employeeData ? `${employeeData.eName} ${employeeData.eSurname}` : 'Kullanıcı'}
                  </h3>
                  <p>
                    {employeeData ? employeeData.eMail : 'email@example.com'}
                  </p>
                </div>
              </div>
              <div className="dropdown-divider"></div>
              {/* "user-dropdown-menu": Bootstrap'in global .dropdown-menu
                  (display: none) kuralıyla çakışmaması için özel sınıf adı */}
              <ul className="user-dropdown-menu">
                <li>
                  <button onClick={handleProfileNavigation}>
                    <FaUserCircle />
                    <span>Profil</span>
                  </button>
                </li>
              </ul>
              <div className="dropdown-divider"></div>
              <div style={{ padding: '8px 0' }}>
                <button 
                  onClick={handleLogout} 
                  className="logout-button"
                  style={{ 
                    color: '#ef4444',
                    fontWeight: 'bold',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    background: 'none',
                    border: 'none',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <FaSignOutAlt style={{ marginRight: '12px' }} />
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <MyTasksModal isOpen={showMyTasks} onClose={handleCloseMyTasks} />
    </header>
  );
};

export default TopBar;